import { Injectable } from '@angular/core'

import { Environment } from './environment.interface'

@Injectable({
  providedIn: 'root',
})
export class VerityEnvironmentService {
  private config!: Environment

  constructor() {
    this.loadEnvironmentConfig()
  }

  private loadEnvironmentConfig(): void {
    const hostname = window.location.hostname
    const protocol = window.location.protocol
    const port = window.location.port

    // Determine environment based on hostname
    const environment = this.getEnvironmentFromHostname(hostname)

    this.config = this.buildConfigForEnvironment(
      environment,
      protocol,
      hostname,
    )

    // Log environment detection in non-production environments only
    if (environment !== 'production') {
      // eslint-disable-next-line no-console
      console.log('Environment Detection:', {
        hostname,
        environment,
        protocol,
        port,
      })
      // eslint-disable-next-line no-console
      console.log('Environment Configuration:', {
        apiUrl: this.config.api,
        production: this.config.production,
        environment: environment,
      })
    }
  }

  private getEnvironmentFromHostname(hostname: string): string {
    // For testing - check URL parameter first
    const urlParams = new URLSearchParams(window.location.search)
    const testEnv = urlParams.get('testEnv')
    if (
      testEnv &&
      ['local', 'development', 'staging', 'production', 'dr'].includes(testEnv)
    ) {
      return testEnv
    }

    if (hostname.includes('localhost') || hostname === '127.0.0.1') {
      return 'local'
    } else if (hostname === 'portal-frontend') {
      return 'ci'
    } else if (hostname.includes('hunter.dev-verity.')) {
      return 'development'
    } else if (hostname.includes('hunter.staging-verity.')) {
      return 'staging'
    } else if (hostname.includes('hunter.dr-verity.')) {
      return 'dr'
    } else if (
      hostname.includes('hunter.prod-verity.') ||
      hostname.includes('verity.intel471.com') ||
      hostname.includes('hunter-cname.cyborgsecurity.io') ||
      hostname.includes('hunter.cyborgsecurity.io')
    ) {
      return 'production'
    } else {
      // Default to local for unknown hosts
      return 'local'
    }
  }

  private buildConfigForEnvironment(
    environment: string,
    protocol: string,
    hostname: string,
  ): Environment {
    const baseConfig = {
      calendlyUrl: this.getCalendlyUrl(environment),
      verity: {
        auth0Url: this.getAuth0Url(environment),
        allowedOrigins: this.getAllowedOrigins(environment),
        appUrl: this.getVerityAppUrl(environment),
      },
      tracking: this.getTrackingConfig(environment),
    }

    switch (environment) {
      case 'production':
        return {
          production: true,
          api: 'https://api.hunter.cyborgsecurity.io',
          ...baseConfig,
        }

      case 'dr':
        return {
          production: true,
          api: 'https://api.hunter.dr-verity.intel471.cloud',
          ...baseConfig,
        }

      case 'staging':
        return {
          production: false,
          api: 'https://api.hunter.staging-verity.intel471.cloud',
          ...baseConfig,
        }

      case 'development':
        return {
          production: false,
          api: 'https://api.hunter.dev-verity.intel471.cloud',
          ...baseConfig,
        }

      case 'ci':
        return {
          production: false,
          api: 'http://portal-api:3000',
          ...baseConfig,
        }

      case 'local':
      default:
        return {
          production: false,
          api: `${protocol}//${hostname}:3000`,
          ...baseConfig,
        }
    }
  }

  private getCalendlyUrl(environment: string): string {
    const calendlyMap: { [key: string]: string } = {
      production:
        'https://calendly.com/intel471experts/30min?hide_gdpr_banner=1&primary_color=e9002a',
      dr: 'https://calendly.com/intel471experts/30min?hide_gdpr_banner=1&primary_color=e9002a',
      staging:
        'https://calendly.com/hunter-dev/30min?hide_gdpr_banner=1&primary_color=e9002a',
      development:
        'https://calendly.com/hunter-dev/30min?hide_gdpr_banner=1&primary_color=e9002a',
      local:
        'https://calendly.com/hunter-dev/30min?hide_gdpr_banner=1&primary_color=e9002a',
    }

    return calendlyMap[environment] || calendlyMap.development
  }

  private getAuth0Url(environment: string): string {
    const auth0Map: { [key: string]: string } = {
      production: 'https://intel471-prod.us.auth0.com/',
      dr: 'https://intel471-prod.us.auth0.com/',
      staging: 'https://intel471-staging.us.auth0.com/',
      development: 'https://intel471-dev.us.auth0.com/',
      local: 'https://intel471-dev.us.auth0.com/',
    }

    return auth0Map[environment] || auth0Map.local
  }

  private getVerityAppUrl(environment: string): string {
    const appUrlMap: { [key: string]: string } = {
      production: 'https://verity.intel471.com',
      dr: 'https://dr-verity.intel471.com',
      staging: 'https://merlin-ui.staging.intel471.cloud',
      development: 'https://merlin-ui.dev.intel471.cloud',
      local: 'https://merlin-ui.dev.intel471.cloud',
    }

    return appUrlMap[environment] || appUrlMap.development
  }

  private getAllowedOrigins(environment: string): string[] {
    const originsMap: { [key: string]: string[] } = {
      production: ['https://verity.intel471.com'],
      dr: ['https://dr-verity.intel471.com'],
      staging: ['https://merlin-ui.staging.intel471.cloud'],
      development: [
        'http://localhost:3000',
        'https://merlin-ui.dev.intel471.cloud',
      ], // http://localhost:3000 allowed local Verity devs to load Hunter
      local: ['http://localhost:3000', 'http://localhost:4200'],
    }

    return originsMap[environment] || originsMap.development
  }

  private getTrackingConfig(environment: string) {
    const baseServiceName = 'hunter-portal-frontend'
    const isLocal = environment === 'local'

    return {
      enabled: true,
      devMode: environment !== 'production' && environment !== 'dr',
      trackers: {
        elastic: {
          serverUrl: isLocal
            ? 'http://localhost:8200'
            : 'https://211cacf861bf426e8efe42151841c466.apm.us-east-1.aws.cloud.es.io',
          serviceName: `${baseServiceName}-${environment}`,
          environment,
          active: true,
          breakdownMetrics: true,
          distributedTracingOrigins: this.getAllowedOrigins(environment),
        },
        fullstory: {
          orgId: '1453KT',
          devMode: environment !== 'production' && environment !== 'dr',
        },
        churnzero: {
          appKey: '1!oZHPc4uS8We8D2AhNBNtdOb-bsVlv3vgGbTPnMLPY-st1548',
          trackingUrl: 'https://intel471.us1app.churnzero.net/i',
        },
      },
    }
  }

  get environment(): Environment {
    return this.config
  }

  get apiUrl(): string {
    return this.config.api
  }

  get production(): boolean {
    return this.config.production
  }

  get trackingConfig() {
    return this.config.tracking
  }

  get calendlyUrl(): string {
    return this.config.calendlyUrl
  }

  get verityConfig() {
    return {
      ...this.config.verity,
      allowedOrigins: this.config.verity?.allowedOrigins || [],
      auth0Url: this.config.verity?.auth0Url || '',
      appUrl: this.config.verity?.appUrl || '',
    }
  }
}
